import { useNavigate } from 'react-router-dom';
import { useFormContext, Controller } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { ROUTE_LABS_ORDER_STEP_1, ROUTE_LABS_ORDER_STEP_3 } from '../../../../constants/routes';
import type { LabsOrderFormData } from '../labs-order.page';
import { useLabOrder } from '../labs-order.page';
import {
  useCreateLabOrderV1LabsOrdersPost,
  usePatchLabOrderV1LabsOrdersOrderIdPatch,
} from '../../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { useGetMeV1PersonsMeGet } from '../../../../api/generated-catch-backend/persons/persons';
import type {
  CreateLabOrderRequest,
  BiologicalSex,
} from '../../../../api/generated-catch-backend/model';
import { LabsOrderStepLayout, FormInput, FormSelect } from '../components';

// Gender options for lab order
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Prefer not to answer' },
];

// US States
const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

export function Step2UserInfo() {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useFormContext<LabsOrderFormData>();

  const { activeOrder, isLoadingOrder, refetchOrder, refetchOrdersList } = useLabOrder();
  const createOrderMutation = useCreateLabOrderV1LabsOrdersPost();
  const patchOrderMutation = usePatchLabOrderV1LabsOrdersOrderIdPatch();

  // Fetch person data to use as fallback
  const {
    data: personData,
    isLoading: isLoadingPerson,
    error: personError,
  } = useGetMeV1PersonsMeGet({
    query: {
      // 404 is OK - user might not have a person record yet
      retry: false,
    },
  });

  // Helper function to merge order and person data, preferring order data
  const getMergedFormData = (): Partial<LabsOrderFormData> => {
    const biologicalSex = activeOrder?.biologicalSex ?? personData?.biologicalSex;

    return {
      firstName: activeOrder?.legalFirstName ?? personData?.legalFirstName ?? '',
      lastName: activeOrder?.legalLastName ?? personData?.legalLastName ?? '',
      email: activeOrder?.email ?? personData?.email ?? '',
      phoneNumber: activeOrder?.phoneNumber ?? personData?.phoneNumber ?? '',
      // Only set gender if we have a valid value, otherwise let form require selection
      ...(biologicalSex && { gender: biologicalSex as 'male' | 'female' | 'other' | 'unknown' }),
      dateOfBirth: activeOrder?.dateOfBirth ?? personData?.dateOfBirth ?? '',
      address: {
        street: activeOrder?.addressLine1 ?? personData?.addressLine1 ?? '',
        apartmentSuite: activeOrder?.addressLine2 ?? personData?.addressLine2 ?? '',
        city: activeOrder?.city ?? personData?.city ?? '',
        state: activeOrder?.state ?? personData?.state ?? '',
        zipCode: activeOrder?.postalCode ?? personData?.postalCode ?? '',
      },
    };
  };

  // Prefill form when data is loaded
  useEffect(() => {
    // Wait for both queries to finish loading
    if (isLoadingOrder || isLoadingPerson) {
      return;
    }

    // If we have either order or person data, prefill the form
    if (activeOrder || personData) {
      reset(getMergedFormData());
    }
  }, [activeOrder, personData, isLoadingOrder, isLoadingPerson, reset]);

  const onSubmit = async (data: LabsOrderFormData) => {
    setApiError(null);

    try {
      // Gender is validated by schema and maps directly to BiologicalSex
      const biologicalSex: BiologicalSex = data.gender as BiologicalSex;

      if (activeOrder && activeOrder.orderId) {
        // Update existing order using PATCH with change_patient_details operation
        await patchOrderMutation.mutateAsync({
          orderId: activeOrder.orderId,
          data: {
            operation: 'change_patient_details',
            data: {
              legalFirstName: data.firstName,
              legalLastName: data.lastName,
              dateOfBirth: data.dateOfBirth,
              biologicalSex,
              phoneNumber: data.phoneNumber || undefined,
              email: data.email || undefined,
              addressLine1: data.address.street || undefined,
              addressLine2: data.address.apartmentSuite || undefined,
              city: data.address.city || undefined,
              state: data.address.state || undefined,
              postalCode: data.address.zipCode || undefined,
            },
          },
        });
      } else {
        // Create new order
        const createRequest: CreateLabOrderRequest = {
          legalFirstName: data.firstName,
          legalLastName: data.lastName,
          dateOfBirth: data.dateOfBirth,
          biologicalSex,
          phoneNumber: data.phoneNumber || undefined,
          email: data.email || undefined,
          addressLine1: data.address.street || undefined,
          addressLine2: data.address.apartmentSuite || undefined,
          city: data.address.city || undefined,
          state: data.address.state || undefined,
          postalCode: data.address.zipCode || undefined,
        };

        await createOrderMutation.mutateAsync({ data: createRequest });
      }

      await refetchOrdersList();

      navigate(ROUTE_LABS_ORDER_STEP_3);
    } catch (error) {
      console.error('Error submitting patient details:', error);
      setApiError('Failed to submit patient details. Please try again.');
    }
  };

  const handleBack = () => {
    navigate(ROUTE_LABS_ORDER_STEP_1);
  };

  // Show loading state while fetching data
  const isLoading = isLoadingOrder || isLoadingPerson;

  return (
    <LabsOrderStepLayout currentStep={2} onBack={handleBack}>
      <p className="mb-6 text-[14px] leading-[170%] text-[#3B3B3B] opacity-80">
        We'll need to confirm your information so the lab can match your results to your ID.
      </p>

      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-[#9E9E9E]">Loading your information...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="firstName"
                  label="First Name"
                  type="text"
                  placeholder="Enter First Name"
                  error={errors.firstName?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="lastName"
                  label="Last Name"
                  type="text"
                  placeholder="Enter Last Name"
                  error={errors.lastName?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>

          {/* Email and Phone */}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="phoneNumber"
                  label="Phone Number"
                  type="tel"
                  placeholder="Enter Phone Number"
                  error={errors.phoneNumber?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="email"
                  label="Email"
                  type="email"
                  placeholder="Enter Email"
                  error={errors.email?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>

          {/* Gender and Date of Birth */}
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <FormSelect
                  id="gender"
                  label="Biological Sex"
                  options={[{ value: '', label: 'Select' }, ...GENDER_OPTIONS]}
                  error={errors.gender?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  error={errors.dateOfBirth?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>

          {/* Address */}
          <div className="space-y-4">
            <Controller
              name="address.street"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="address.street"
                  label="Address Line 1"
                  type="text"
                  placeholder="Enter Address"
                  error={errors.address?.street?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            <Controller
              name="address.apartmentSuite"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="address.apartmentSuite"
                  label="Address Line 2"
                  type="text"
                  placeholder="Enter Address"
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            <Controller
              name="address.city"
              control={control}
              render={({ field }) => (
                <FormInput
                  id="address.city"
                  label="City"
                  type="text"
                  placeholder="Enter City"
                  error={errors.address?.city?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="address.state"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    id="address.state"
                    label="State"
                    options={US_STATES}
                    error={errors.address?.state?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />

              <Controller
                name="address.zipCode"
                control={control}
                render={({ field }) => (
                  <FormInput
                    id="address.zipCode"
                    label="Zip Code"
                    type="text"
                    placeholder="Enter Zip"
                    error={errors.address?.zipCode?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* API Error Display */}
        {apiError && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200">
            <p className="text-sm text-red-800">{apiError}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={createOrderMutation.isPending || patchOrderMutation.isPending}
            className="w-full rounded-full bg-[#7CF1C7] px-8 py-3.5 text-center text-[17px] font-bold leading-[26px] text-[#183344] transition-all hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createOrderMutation.isPending || patchOrderMutation.isPending
              ? 'Submitting...'
              : 'Continue'}
          </button>
        </div>
      </form>
      )}
    </LabsOrderStepLayout>
  );
}